// DeviceListToolbar – selected count, search, filter by request, columns, Offline ops, Submit command, loading/error.

import React, { JSX, useRef, useState } from 'react';
import { Link2, Plus, RefreshCw, Send, SlidersHorizontal } from 'lucide-react';
import type { ExtraColumnKey } from '../../../../types/DeviceListTypes';
import DeviceIdTableFilterBar from '../../shared/DeviceIdTableFilterBar';
import { BTN_ICON, BTN_PRIMARY, BTN_SECONDARY, INPUT } from '../utils/deviceListConstants';
import { ColumnsMenu, DeviceTableColumnsTrigger } from './ColumnsMenu';
import { FiltersMenu } from './FiltersMenu';

import type { DeviceListToolbarProps } from '../types/DeviceListViewProps';

export type { DeviceListToolbarProps };

export function DeviceListToolbar({
  isAdmin,
  selectedIds,
  onClearSelection,
  searchQuery,
  onSearchChange,
  onOpenSelectRequest,
  columns,
  visibleMasterColumns,
  visibleExtraColumns,
  onToggleMasterColumn,
  onToggleExtraColumn,
  columnsMenuOpen,
  setColumnsMenuOpen,
  onOpenConfigure,
  onSubmitCommand,
  locationFilterActive = false,
  onClearLocationFilter,
  showAdjacentPairs = false,
  onToggleAdjacentPairs = () => {},
  activeFilterCount = 0,
  onClearFilters,
  filters,
  onChangeFilters,
  onCsvFilterIdsChange,
  lifecycleFilterEnabled = true,
  activeRequestId,
  requestFilterIds,
  onClearRequest,
  onRefresh,
  refreshing = false,
  onOpenAddDevice,
}: DeviceListToolbarProps): JSX.Element {
  const columnsButtonRef = useRef<HTMLButtonElement>(null);
  const filtersButtonRef = useRef<HTMLButtonElement>(null);
  const [filtersMenuOpen, setFiltersMenuOpen] = useState(false);
  const hasSelection = selectedIds.length > 0;
  const selectionBarClass = hasSelection
    ? 'border-blue-200/80 bg-blue-50/60 text-blue-950 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-200'
    : 'border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-slate-300';
  const hasActiveFilters = activeFilterCount > 0;
  const filterBarClass = hasActiveFilters
    ? 'border-blue-200/80 bg-blue-50/60 text-blue-950 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-200'
    : 'border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-slate-300';
  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch lg:justify-between lg:gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <div
              className={[
                'inline-flex h-9 shrink-0 items-center gap-2 rounded-lg border px-3 text-[11px] shadow-sm',
                selectionBarClass,
              ].join(' ')}
            >
              <span className="font-medium text-current/80">Selected</span>
              <span className="tabular-nums font-semibold">{selectedIds.length}</span>
              <span className="text-slate-300" aria-hidden>
                |
              </span>
              <button
                type="button"
                className="font-medium text-blue-700 hover:text-blue-900 hover:underline disabled:text-slate-400 disabled:no-underline dark:text-blue-300 dark:hover:text-blue-100 dark:disabled:text-slate-500 dark:text-blue-300 dark:hover:text-blue-100 dark:disabled:text-slate-500 dark:text-blue-300 dark:hover:text-blue-100 dark:disabled:text-slate-500"
                disabled={!hasSelection}
                onClick={onClearSelection}
              >
                Clear
              </button>
            </div>

            <div
              className={[
                'inline-flex h-9 shrink-0 items-center gap-2 rounded-lg border px-3 text-[11px] shadow-sm',
                filterBarClass,
              ].join(' ')}
            >
              <span className="font-medium text-current/80">Filters</span>
              <span className="tabular-nums font-semibold">{activeFilterCount}</span>
              <span className="text-slate-300" aria-hidden>
                |
              </span>
              <button
                type="button"
                className="font-medium text-blue-700 hover:text-blue-900 hover:underline disabled:text-slate-400 disabled:no-underline dark:text-blue-300 dark:hover:text-blue-100 dark:disabled:text-slate-500"
                disabled={!hasActiveFilters}
                onClick={onClearFilters}
              >
                Clear
              </button>
            </div>

            <div className="relative min-w-0 flex-1 sm:max-w-md lg:max-w-lg">
              <DeviceIdTableFilterBar
                value={searchQuery}
                onChange={onSearchChange}
                enableScanner
                persistModeKey="fleetmanager:deviceList:scanEntryMode"
                className="[&_input]:pl-8"
                inputClassName={`${INPUT} h-9 w-full min-w-0 text-xs`}
              />
            </div>
            {locationFilterActive && (
              <div className="inline-flex h-9 shrink-0 items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/80 px-2.5 text-[11px] text-emerald-900 shadow-sm dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-200">
                <span className="font-medium">Location filter active</span>
                <button
                  type="button"
                  className="rounded border border-emerald-200 dark:border-emerald-700/60 bg-white dark:bg-slate-900 px-1.5 py-0.5 text-[10px] font-semibold leading-none hover:bg-emerald-50"
                  onClick={onClearLocationFilter}
                  aria-label="Clear location filter"
                >
                  ×
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:justify-end">
            <div className="inline-flex items-center gap-1">
              {onRefresh && (
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={refreshing}
                  aria-label="Refresh device list"
                  title="Refresh device list"
                  className={BTN_ICON}
                >
                  <RefreshCw
                    className={['h-4 w-4', refreshing ? 'animate-spin' : ''].filter(Boolean).join(' ')}
                    aria-hidden
                  />
                </button>
              )}

              {isAdmin && onOpenAddDevice && (
                <button
                  type="button"
                  onClick={onOpenAddDevice}
                  aria-label="Add device"
                  title="Add device to the mappings table"
                  className={BTN_ICON}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                </button>
              )}

              <button
                type="button"
                onClick={() => onToggleAdjacentPairs(!showAdjacentPairs)}
                aria-pressed={showAdjacentPairs}
                aria-label="Pair front/rear rows"
                title={showAdjacentPairs ? 'Unpair front/rear rows' : 'Pair front/rear rows'}
                className={
                  showAdjacentPairs
                    ? 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-blue-300 bg-blue-50 text-blue-700 shadow-sm transition-all duration-100 hover:bg-blue-100 dark:h-8 dark:w-8 dark:border-blue-700/60 dark:bg-blue-950/50 dark:text-blue-200 dark:hover:bg-blue-900/50'
                    : BTN_ICON
                }
              >
                <Link2 className="h-4 w-4" aria-hidden />
              </button>

              <div className="relative">
                <button
                  ref={filtersButtonRef}
                  type="button"
                  onClick={() => setFiltersMenuOpen((v) => !v)}
                  aria-expanded={filtersMenuOpen}
                  aria-haspopup="dialog"
                  aria-label="Filters"
                  title="Filters"
                  className={
                    activeFilterCount > 0 || activeRequestId
                      ? 'relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-blue-300 bg-blue-50 text-blue-700 shadow-sm transition-all duration-100 hover:bg-blue-100 dark:h-8 dark:w-8 dark:border-blue-700/60 dark:bg-blue-950/50 dark:text-blue-200 dark:hover:bg-blue-900/50'
                      : `relative ${BTN_ICON}`
                  }
                >
                  <SlidersHorizontal className="h-4 w-4" aria-hidden />
                  {(activeFilterCount > 0 || activeRequestId) && (
                    <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold leading-none text-white">
                      {activeFilterCount + (activeRequestId ? 1 : 0)}
                    </span>
                  )}
                </button>
                <FiltersMenu
                  open={filtersMenuOpen}
                  onClose={() => setFiltersMenuOpen(false)}
                  anchorRef={filtersButtonRef}
                  filters={filters}
                  onChangeFilters={onChangeFilters}
                  onCsvFilterIdsChange={onCsvFilterIdsChange}
                  lifecycleFilterEnabled={lifecycleFilterEnabled}
                  onOpenSelectRequest={onOpenSelectRequest}
                  activeRequestId={activeRequestId}
                  requestFilterIds={requestFilterIds}
                  onClearRequest={onClearRequest}
                />
              </div>

              <div className="relative">
                <DeviceTableColumnsTrigger
                  buttonRef={columnsButtonRef}
                  open={columnsMenuOpen}
                  onToggle={() => setColumnsMenuOpen((v) => !v)}
                />
                <ColumnsMenu
                  open={columnsMenuOpen}
                  onClose={() => setColumnsMenuOpen(false)}
                  anchorRef={columnsButtonRef}
                  columns={columns}
                  visibleMasterColumns={visibleMasterColumns}
                  visibleExtraColumns={visibleExtraColumns}
                  onToggleMasterColumn={onToggleMasterColumn}
                  onToggleExtraColumn={onToggleExtraColumn}
                />
              </div>
            </div>

            <div className="mx-0.5 hidden h-7 w-px bg-gray-200 dark:bg-slate-700 sm:block" aria-hidden />

            {isAdmin && (
              <button
                type="button"
                className={BTN_SECONDARY}
                disabled={!hasSelection}
                onClick={onOpenConfigure}
              >
                Offline operations
              </button>
            )}

            <button
              type="button"
              className={`${BTN_PRIMARY} inline-flex items-center gap-1.5 shadow-sm`}
              disabled={!hasSelection}
              onClick={onSubmitCommand}
            >
              <Send className="h-3.5 w-3.5 opacity-90" aria-hidden />
              Submit command
            </button>
          </div>
        </div>

      </div>
    </>
  );
}
